import React, { useState } from 'react';
import { herbBatches } from '../data/herbData';

const ScanQR = () => {
  const [batchId, setBatchId] = useState('');
  const [batchData, setBatchData] = useState(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  const handleScan = () => {
    setError('');
    setIsScanning(true);
    
    // Simulate scanning delay
    setTimeout(() => {
      const batch = herbBatches[batchId.toUpperCase()];
      if (batch) {
        setBatchData(batch);
        setError('');
      } else {
        setBatchData(null);
        setError('Batch not found. Try ASH001 or TUL002 for demo data.');
      }
      setIsScanning(false);
    }, 1500);
  };

  const handleInputChange = (e) => {
    setBatchId(e.target.value);
    if (error) setError('');
    if (batchData) setBatchData(null);
  };

  const StatusBadge = ({ status, label }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {status ? '✓' : '✗'} {label}
    </span>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Scan QR Code</h1>
        <p className="mt-2 text-lg text-gray-600">
          Enter a batch ID to view herb traceability information
        </p>
      </div>

      {/* QR Scanner Simulation */}
      <div className="max-w-md mx-auto mb-8">
        <div className="card text-center">
          <div className="mb-6">
            <div className="w-48 h-48 mx-auto bg-gray-100 rounded-lg flex items-center justify-center border-4 border-dashed border-gray-300">
              {isScanning ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Scanning...</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-6xl mb-2">📱</div>
                  <p className="text-sm text-gray-600">QR Scanner Placeholder</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="batchId" className="block text-sm font-medium text-gray-700 mb-2">
                Or enter Batch ID manually:
              </label>
              <input
                type="text"
                id="batchId"
                value={batchId}
                onChange={handleInputChange}
                placeholder="e.g., ASH001"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <button
              onClick={handleScan}
              disabled={!batchId.trim() || isScanning}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isScanning ? 'Scanning...' : 'Scan Batch'}
            </button>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            <div className="text-xs text-gray-500">
              <p>Demo batch IDs: ASH001, TUL002</p>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Data Display */}
      {batchData && (
        <div className="space-y-6 animate-fade-in">
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Batch verified successfully!
                </p>
                <p className="text-sm text-green-600">
                  Blockchain hash: {batchData.blockchain.hash}
                </p>
              </div>
            </div>
          </div>

          {/* Herb Overview */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{batchData.herbName}</h2>
                <p className="text-gray-600 italic">{batchData.botanicalName}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Batch ID</p>
                <p className="text-lg font-mono font-semibold text-primary-600">{batchData.id}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Farmer Information */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Farmer Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Farmer Name</p>
                  <p className="text-lg font-medium text-gray-900">{batchData.farmer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-gray-900">{batchData.farmer.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Harvest Date</p>
                  <p className="text-gray-900">{new Date(batchData.harvestDate).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Quick Test Results */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quality Tests</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Pesticide</span>
                  <span className="text-green-600 font-medium">{batchData.labTests.pesticide}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Heavy Metals</span>
                  <span className="text-green-600 font-medium">{batchData.labTests.heavyMetals}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Microbiology</span>
                  <span className="text-green-600 font-medium">{batchData.labTests.microbiology}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Tested: {new Date(batchData.labTests.testDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sustainability Quick View */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sustainability</h3>
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={batchData.sustainability.organic} label="Organic" />
              <StatusBadge status={batchData.sustainability.fairTrade} label="Fair Trade" />
              <StatusBadge status={batchData.sustainability.carbonNeutral} label="Carbon Neutral" />
              <StatusBadge status={batchData.sustainability.waterConservation} label="Water Conservation" />
            </div>
          </div>

          {/* Processing Timeline */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Processing Timeline</h3>
            <div className="space-y-3">
              {batchData.processing.map((step, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{step.step}</span>
                      <span className="text-sm text-gray-500">{new Date(step.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* View Full Details Button */}
          <div className="text-center">
            <a
              href="/provenance"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              View Full Provenance Details
              <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanQR;
